import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Quote, Star } from 'lucide-react';
import type { Testimonial } from '../../types/salesPage';
interface TestimonialsCarouselProps {
  title: string;
  subtitle: string;
  stats: {
    value: string;
    label: string;
  }[];
  items: Testimonial[];
}
const AUTOPLAY_MS = 4500;
export function TestimonialsCarousel({
  title,
  subtitle,
  stats,
  items
}: TestimonialsCarouselProps) {
  const [perView, setPerView] = useState(1);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    const update = () => {
      const width = window.innerWidth;
      setPerView(width >= 1024 ? 3 : width >= 640 ? 2 : 1);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  const maxIndex = Math.max(0, items.length - perView);
  useEffect(() => {
    setIndex(current => Math.min(current, maxIndex));
  }, [maxIndex]);
  const goTo = useCallback((next: number) => {
    if (next > maxIndex) return setIndex(0);
    if (next < 0) return setIndex(maxIndex);
    return setIndex(next);
  }, [maxIndex]);
  useEffect(() => {
    if (paused) return;
    const id = window.setTimeout(() => goTo(index + 1), AUTOPLAY_MS);
    return () => window.clearTimeout(id);
  }, [index, paused, goTo]);
  return <section aria-labelledby="testimonials-title" aria-roledescription="carrusel" className="overflow-hidden border-t border-white/[0.07] bg-ink px-5 py-14 sm:px-6 md:py-20" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)} data-id="element-282" data-mp="1mfbh06m8vrdm">
      <div className="mx-auto max-w-5xl" data-id="element-283" data-mp="12i3l3844aq7">
        <motion.header initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-70px'
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className="text-center" data-id="element-284" data-mp="13jn7w3725jj">
          <h2 id="testimonials-title" className="font-serif text-[24px] font-bold leading-tight text-parchment sm:text-[30px] md:text-[36px]" data-id="element-285" data-mp="216kk865nuxh5">
            {title}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-[14.5px] font-light leading-relaxed text-ash md:text-[16px]" data-id="element-286" data-mp="2b5a4u6oxyw13">
            {subtitle}
          </p>
        </motion.header>

        <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-y border-white/[0.08] py-4" data-id="element-287" data-mp="2k3lqrvev55n7">
          {stats.map((stat, i) => <motion.li key={stat.label} initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.4,
          delay: i * 0.08,
          ease: 'easeOut'
        }} className="flex items-baseline gap-2" data-id="element-288" data-mp="2oxgbn267fqnv" data-mpi={i}>
              <span className="font-serif text-[20px] font-bold text-gold-soft md:text-[24px]" data-id="element-289" data-mp="r8b32jl1665r">
                {stat.value}
              </span>
              <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-ash" data-id="element-290" data-mp="n8amasaglivj">
                {stat.label}
              </span>
            </motion.li>)}
        </ul>

        <div className="relative mt-8" data-id="element-291" data-mp="da3asoyxvt0x">
          <div className="-mx-2 overflow-hidden" data-id="element-292" data-mp="1avs8m8ayfor4">
            <motion.ul className="flex cursor-grab active:cursor-grabbing" drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.12} onDragStart={() => setPaused(true)} onDragEnd={(_, info) => {
            if (info.offset.x < -60) goTo(index + 1);else if (info.offset.x > 60) goTo(index - 1);
            setPaused(false);
          }} animate={{
            x: `-${index * (100 / perView)}%`
          }} transition={{
            type: 'spring',
            stiffness: 90,
            damping: 20,
            mass: 0.6
          }} data-id="element-293" data-mp="1axlgfqxb0f0p">
              {items.map((item, i) => {
              const isVisible = i >= index && i < index + perView;
              return <li key={item.name + item.quote.slice(0, 12)} className="shrink-0 px-2" style={{
                width: `${100 / perView}%`
              }} aria-hidden={!isVisible} data-id="element-294" data-mp="zg4f1yt01d7b" data-mpi={i}>
                    <motion.figure animate={{
                  opacity: isVisible ? 1 : 0.25,
                  scale: isVisible ? 1 : 0.94,
                  filter: isVisible ? 'blur(0px)' : 'blur(1px)'
                }} transition={{
                  duration: 0.45,
                  ease: 'easeOut'
                }} whileHover={{
                  y: -6
                }} className="flex h-full flex-col rounded-sm border border-white/10 bg-panel px-5 py-6 transition-colors duration-300 hover:border-gold/40" data-id="element-295" data-mp="1s64c5bh9dlzm">
                      <div className="flex items-center justify-between" data-id="element-296" data-mp="4csmqv4b6t89">
                        <Quote aria-hidden="true" className="h-5 w-5 text-gold/70" data-id="element-297" data-mp="3vjlr27uef8h0" />
                        <span className="flex gap-0.5" aria-label="5 de 5 estrellas" data-id="element-298" data-mp="d6xb97vyzskm">
                          {Array.from({
                        length: 5
                      }).map((_, starIndex) => <motion.span key={starIndex} initial={{
                        opacity: 0,
                        scale: 0.6
                      }} whileInView={{
                        opacity: 1,
                        scale: 1
                      }} viewport={{
                        once: true
                      }} transition={{
                        duration: 0.3,
                        delay: 0.1 + starIndex * 0.06
                      }} data-id="element-299" data-mp="97g6kgodazgi" data-mpi={starIndex}>
                              <Star aria-hidden="true" className="h-3 w-3 fill-gold-soft text-gold-soft" data-id="element-300" data-mp="15vcobovjk0tb" />
                            </motion.span>)}
                        </span>
                      </div>

                      <blockquote className="mt-4 flex-1 font-serif text-[15.5px] italic leading-[1.6] text-ivory/90 md:text-[16.5px]" data-id="element-301" data-mp="3hf1k746made">
                        {item.quote}
                      </blockquote>

                      <figcaption className="mt-5 flex items-center gap-3 border-t border-white/[0.08] pt-4" data-id="element-302" data-mp="civ1fkxdeu3d">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gold/35 bg-gold/[0.07] font-mono text-[11px] text-gold-soft" data-id="element-303" data-mp="3bz1hphapl5sy">
                          {item.initials}
                        </span>
                        <span className="min-w-0" data-id="element-304" data-mp="3pp4z9uyn8bc2">
                          <span className="block font-serif text-[14.5px] font-semibold text-ivory" data-id="element-305" data-mp="3ppwrqwwy1sy4">
                            {item.name}
                          </span>
                          <span className="block font-mono text-[9.5px] uppercase tracking-[0.1em] text-ash/80" data-id="element-306" data-mp="2x82dsjktj5fp">
                            {item.detail}
                          </span>
                        </span>
                        <span className="ml-auto shrink-0 rounded-sm border border-gold/25 px-2 py-1 font-mono text-[8.5px] uppercase tracking-[0.12em] text-gold-soft" data-id="element-307" data-mp="2krji3jk7zb71">
                          {item.badge}
                        </span>
                      </figcaption>
                    </motion.figure>
                  </li>;
            })}
            </motion.ul>
          </div>

          <div className="mt-7 flex items-center justify-center gap-4" data-id="element-308" data-mp="21yysqc3qbo7z">
            <button type="button" onClick={() => goTo(index - 1)} aria-label="Testimonio anterior" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-ash transition-colors duration-200 hover:border-gold/50 hover:text-gold-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-soft" data-id="element-309" data-mp="1x047l3iahnkp">
              <ChevronLeft aria-hidden="true" className="h-4 w-4" data-id="element-310" data-mp="36xaq4o2n5rza" />
            </button>

            <ul className="flex items-center gap-2" data-id="element-311" data-mp="3bx6uwpj5xnfw">
              {Array.from({
              length: maxIndex + 1
            }).map((_, i) => <li key={`dot-${i}`} data-id="element-312" data-mp="2xw4b6jfn4p85" data-mpi={i}>
                  <button type="button" onClick={() => goTo(i)} aria-label={`Ir al testimonio ${i + 1}`} aria-current={i === index} className="group relative flex h-4 items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-soft" data-id="element-313" data-mp="2xz8k1u41w955">
                    <motion.span animate={{
                  width: i === index ? 26 : 6
                }} transition={{
                  duration: 0.3,
                  ease: 'easeOut'
                }} className={`relative block h-1.5 overflow-hidden rounded-full ${i === index ? 'bg-white/15' : 'bg-white/20 group-hover:bg-white/40'}`} data-id="element-314" data-mp="2eahq67x2swqc">
                      {i === index && <motion.span key={`progress-${index}-${paused}`} initial={{
                    width: paused ? '100%' : '0%'
                  }} animate={{
                    width: '100%'
                  }} transition={{
                    duration: paused ? 0 : AUTOPLAY_MS / 1000,
                    ease: 'linear'
                  }} className="absolute inset-y-0 left-0 block rounded-full bg-gold" data-id="element-315" data-mp="1lmez63ee3o5o" />}
                    </motion.span>
                  </button>
                </li>)}
            </ul>

            <button type="button" onClick={() => goTo(index + 1)} aria-label="Siguiente testimonio" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-ash transition-colors duration-200 hover:border-gold/50 hover:text-gold-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-soft" data-id="element-316" data-mp="2jc9w494ejwv6">
              <ChevronRight aria-hidden="true" className="h-4 w-4" data-id="element-317" data-mp="7b9nnvpo8sv5" />
            </button>
          </div>
        </div>
      </div>
    </section>;
}